import type { WebRequest } from '../generated/api/WebRequest';
import {
  type FetcherOptions,
  ApiError,
  HttpError,
  getAuditActionData,
  type MakeFetcherOptions,
} from '@pdcfrontendui/staffplan';
import { makeFetcher } from '@pdcfrontendui/staffplan';
import { RefreshJwt } from '../generated/api/Login_api';
import { ApplicationEnum } from '../generated/api/enumLib_api';
import { ClientInfo, Log2 } from '../generated/api/NotificationLog_api';
import platform from 'platform';
import { coerceToError, type PdcError } from '../error/errors';
import { z } from 'zod';
import { isAppJson } from './appJson';

const windowSchema = z.object({
  pdc: z.object({
    shell: z.object({
      buildInfo: z.object({
        version: z.string(),
      }),
    }),
  }),
});

function getCordovaVersion(): string | undefined {
  const result = windowSchema.safeParse(window);
  if (result.success) {
    return result.data.pdc.shell.buildInfo.version;
  } else {
    return undefined;
  }
}

export type FetchOptions = Partial<FetcherOptions & { disableLog: boolean }>;

export class Api {
  private version: string;
  protected applicationId: ApplicationEnum;
  /**
   * Override this in subclasses to set the base URL for the API.
   * Defaults to an empty string.
   */
  public baseUrl: string = '';
  protected fetcher: ReturnType<typeof makeFetcher>;
  private onLogFail?: (error: PdcError) => void;
  protected loggingEnabled: boolean = true;

  constructor(
    args: Omit<MakeFetcherOptions, 'getAuditActionData'> & {
      version: string;
      applicationId: ApplicationEnum;
      onLogFail?: (error: PdcError) => void;
    }
  ) {
    while (this.baseUrl.endsWith('/')) {
      this.baseUrl = this.baseUrl.slice(0, -1);
    }
    this.version = args.version;
    this.applicationId = args.applicationId;
    this.onLogFail = args.onLogFail;
    this.fetcher = makeFetcher({
      applicationId: this.applicationId,
      getAuditActionData,
      getLangCode: args.getLangCode,
      getToken: args.getToken,
      onApiError: args.onApiError,
      onAuthError: args.onAuthError,
      onHttpError: args.onHttpError,
      onValidationError: args.onValidationError,
      removeToken: args.removeToken,
      setToken: args.setToken,
    });
  }

  private async fetchWithoutLogging<T>(
    request: WebRequest<T>,
    options?: Partial<FetcherOptions>
  ): Promise<T> {
    return await this.fetcher<T>(
      // TODO: fetcher postfixes url with `/${WebRequest.method}`
      // Either it should do that and also the web service, or do no postfixing and leave it to this method
      `${this.baseUrl}/api/${request.webService}`,
      request,
      options
    );
  }

  protected async fetch<T>(
    request: WebRequest<T>,
    options?: FetchOptions
  ): Promise<T> {
    try {
      return await this.fetchWithoutLogging(request, options);
    } catch (e: unknown) {
      const error = coerceToError(
        e,
        `Unknown caught value during API call ${request.webService}/${request.method}`
      );
      if (
        this.loggingEnabled &&
        !options?.disableLog &&
        !(error instanceof ApiError && error.isUserDirectedError())
      ) {
        // Note: no await here, because we don't want to block the promise rejection.
        this.log(error, {
          tryIncludeToken: true,
          allowErrors: false,
          // Don't retry logging if the error is an HttpError since it probably won't succeed anyway
          retry: error instanceof HttpError ? 0 : undefined,
        }).catch(() => {
          this.onLogFail?.(error);
        });
      }
      throw error;
    }
  }

  public cancelActiveRequests(
    where?: (req: WebRequest<unknown>) => boolean
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    this.fetcher.cancelActiveRequests(where as any);
  }

  public async isNewVersionAvailable(): Promise<boolean> {
    const response = await fetch('./app.json');
    const json: unknown = await response.json();
    // Assume that a different version means "newer"
    return isAppJson(json) && json.version !== this.version;
  }

  public async refreshJwt(
    options: FetchOptions = { allowErrors: false }
  ): Promise<void> {
    // Always returns true, otherwise it will throw an error
    await this.fetch(new RefreshJwt(), options);
  }

  public async log(
    error: PdcError,
    options: FetchOptions = { allowErrors: false }
  ): Promise<void> {
    const extra: { cordovaVersion?: string } = {
      cordovaVersion: getCordovaVersion(),
    };
    const req = new Log2(
      error.name,
      error.type,
      error instanceof ApiError && error.data
        ? `${error.message}\n\nStacktrace:\n${error.data}`
        : error.message,
      '',
      new ClientInfo(
        platform.description ?? '', // 'Safari 5.1 on Apple iPad (iOS 5.0)'
        platform.os?.toString() ?? '', // 'iOS 5.0'
        platform.name ?? '', // 'Safari'
        this.applicationId,
        this.version,
        JSON.stringify(extra) // Stringified arbitrary data relevant to the client
      )
    );
    await this.fetchWithoutLogging(req, options);
  }
}
